import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Airport } from '../models/airport.model';
import { QuickNavToc } from '../../shared/models/quick-nav-toc.model';
import { BusinessSummary } from '../models/business-summary.model';
import { AirportFeeSummaryComponent } from '../airport-fee-summary/airport-fee-summary.component';
import { AirportService } from '../airport.service';
import { QuickNavComponent } from '../../shared/quick-nav/quick-nav.component';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { MatDialog } from '@angular/material';
import { AddBusinessDialogComponent } from '../add-business-dialog/add-business-dialog.component';
import { CloseDialog } from '../../shared/dialog-close/dialog-close';
import { Router } from '../../../../node_modules/@angular/router';
import { WhatsNewService } from '../../shared/services/whats-new.service';
import { WhatsnewComponent } from '../../whatsnew/whatsnew.component';
import { SubmitNewBusinessComponent } from '../submit-new-business/submit-new-business.component';
import { AnalyticsService } from '../service/google-analytics-4.service';
import { AuthService } from '../../auth/auth/auth.service';


@Component({
  selector: 'app-airport-detail',
  templateUrl: './airport-detail.component.html',
  styleUrls: ['./airport-detail.component.scss']
})
export class AirportDetailComponent implements AfterViewInit, OnInit {
  @Input() airport: Airport;

  @ViewChild(AirportFeeSummaryComponent) feesSection: AirportFeeSummaryComponent;
  @ViewChild('quicknav') quicknav: QuickNavComponent;
  @ViewChild('body', { read: ElementRef }) body: ElementRef;

  toc: QuickNavToc = new QuickNavToc('airport-quicknav', [{
    label: 'Overview',
    fragment: 'overview',
    visible: true
  }, {
    label: 'FBOs',
    fragment: 'fbos',
    visible: true
  }, {
    label: 'Food/Lodging/Activities',
    fragment: 'local',
    visible: true
  }, {
    label: 'Flying Clubs',
    fragment: 'flyingclubs',
    visible: true
  }, /* commented out temporarily per Eric on 3/9/2018
  {
    label: 'Fees',
    fragment: 'fees',
    visible: () => this.feesSection.hasContent
  },*/ {
    label: 'Runways',
    fragment: 'runways',
    visible: true
  }, {
    label: 'TFRs and NOTAMs',
    fragment: 'notams',
    visible: true
  }, {
    label: 'Procedures',
    fragment: 'procedures',
    visible: true
  }, /* {
    label: 'Operations',
    fragment: 'operations',
    visible: true
  }, */ {
    label: 'Comms',
    fragment: 'comms',
    visible: true
  }, {
    label: 'Weather',
    fragment: 'wx',
    visible: true
  }]);

  isFlightDeckAdmin = false;
  editModeSet = false;
  isAirportManager = false;
  oldAirport: Airport;

  constructor( private _whatsNewService: WhatsNewService,
               private _businessManagerService: BusinessManagerService,
               private _toggleService: EditToggleService,
               private _matDialog: MatDialog,
               private _router: Router,
               private _airportService: AirportService,
               private _analyticsService: AnalyticsService,
               private _auth: AuthService
               ) {
  }

  /**
   * Returns a subset of the list of businesses at this airport that either (1) have overview fees reported, or (2)
   * provide fuel. This is used for displaying the fee summary section, which is meant to list all FBOs which may
   * have fees. In the case where fees are not reported, a placeholder message is shown saying the location has not
   * disclosed fees.
   */
  get businessesWithFees(): Array<BusinessSummary> {
    return this.airport.businesses.filter(b => AirportService.shouldShowFees(b));
  }

  async ngOnInit() {
    this.isAirportManager = await this._businessManagerService.userManagesAirport(this.airport.airportId);
    this.isFlightDeckAdmin = await this._businessManagerService.isFlightDeckAdmin();
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.oldAirport = this.airport;

    const showWhatsNew = await this._whatsNewService.shouldShowWhatsNew();
    if (showWhatsNew) {
      const dialogRef = this._matDialog.open(WhatsnewComponent);
      dialogRef.afterClosed().subscribe(result => {
        this._whatsNewService.displayedWhatsNew();
      });
    }
    this._analyticsService.pageView('airport_page', 'Airport Detail');
    this._analyticsService.trackEvent('airport_viewed', this.airport.identifier, this.airport.identifier);
    if (this._auth.authenticated && this._auth.endUser) {
      this._analyticsService.trackEvent('airport_loggedin', this._auth.recid, this._auth.recid);
    } else {
      this._analyticsService.trackEvent('airport_loggedout', '', '');
    }
  }

  /* async ngDoCheck() {
     /
      * This hack is to handle routes coming from the autocomplete search.
      * Without it, an airport manager would look like they could manage airports they don't have permission to manage.
      * The API prevents any actual changes regardless of what the interface shows without this hack.
      /
    if (this.airport && this.oldAirport && this.airport.airportId !== this.oldAirport.airportId) {
      this.oldAirport = this.airport;
      this.isAirportManager = await this._businessManagerService.userManagesAirport(this.airport.airportId);
      if (!this.isAirportManager) {
        this._toggleService.setState(false);
      }
    }
  } */

  ngAfterViewInit() {
    /*
    const $detailBody = $(this.body.nativeElement);
    const $page = $detailBody.closest('.detail-page');
    this.quicknav.setContainerHeight($page.height() + 1000);
    */
  }

  get sortedFBOs(): BusinessSummary[] {
    if (!this.isFlightDeckAdmin) {
      return this.airport.businesses
        .filter(b => b.isFbo)
        .filter(fbo => !fbo.hideBusiness)
        .filter(fbo => !fbo.notApproved)
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });
    }
    return this.airport.businesses
      .filter(b => b.isFbo)
      .sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
  }

  async onReloadAirport(reload: boolean) {
    this.airport = await this._airportService.getAirport(this.airport.identifier, true);
  }

  async reloadAirport(identifier: string) {
    this.airport = await this._airportService.getAirport(identifier, true);
  }

  openAddDialog(facilityType: string): void {
    const dialogRef = this._matDialog.open(AddBusinessDialogComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airport: this.airport,
        facilityType: facilityType
      }
    });
    dialogRef.afterClosed().subscribe(closeDialog => {
      if (closeDialog.cancelButtonClicked) {
        return;
      }
      if (closeDialog.success) {
        let type: string;
        if (facilityType === 'FLYING CLUB') {
          type = 'flyingclub';
        } else if (facilityType === 'BUSINESS') {
          type = 'business';
        }
        this.reloadAirport(this.airport.identifier);
        this._router.navigateByUrl(`/${type}/${closeDialog.payload}`);
      }
    });
  }

  openSubmitNewBusinessDialog(facilityType: string): void {
    const dialogRef = this._matDialog.open(SubmitNewBusinessComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        airport: this.airport,
        facilityType: facilityType
      }
    });
  }
}
